import React from "react";
// Billing Report Page

// Default
import { useState, useEffect } from "react";
import moment from "moment";
import "moment-timezone";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomDataTable from "../../components/table/CustomTable";
import BillingReportBarChart from "../charts/BillingReportBarChart";

import CustomSearch from "../search/Search";
import { formatTime, formatDate } from "../../utils/helper";
import Calendar2 from "../../assets/svg/calendar2";
import DateAndTimePopover from "../popovers/DateAndTimePopover";

const startOfDayIST = moment().tz("Asia/Kolkata").startOf("day").valueOf();
const endOfDayIST = moment().tz("Asia/Kolkata").endOf("day").valueOf();

const getTimeStamp = (newTime: string | number, newDate: Date) => {
  const [hours, minutes] =
    typeof newTime === "string" ? newTime.split(":").map(Number) : [newTime, 0];

  // Create a moment object in IST using newDate
  const istMoment = moment
    .tz(newDate, "Asia/Kolkata")
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

  return istMoment.valueOf(); // Returns UTC timestamp representing the selection in IST
};

const BillingReport: React.FC = () => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [timeRange, setTimeRange] = useState<[number, number]>([0, 1439]);
  const [timeStamp, setTimeStamp] = useState({
    start: startOfDayIST,
    end: endOfDayIST,
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDateChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleDateSubmit = () => {
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));

    console.log(timeRange, startTs, endTs);

    setTimeStamp({
      start: startTs,
      end: endTs,
    });

    setAnchorEl(null);
  };

  const generateRandomData = () => {
    const data = [];
    for (let i = 1; i <= 10; i++) {
      const requestsReceived = Math.floor(Math.random() * 1000);
      const readsShared = Math.floor(Math.random() * 1000);
      data.push({
        meterId: `Meter${i}`,
        requestsReceived,
        readsShared,
        difference: requestsReceived - readsShared,
      });
    }
    return data;
  };

  useEffect(() => {
    setReportData(generateRandomData());
  }, []);

  const downloadCSV = () => {
    const headers = Object.keys(reportData[0]).join(",");
    const rows = reportData
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const COLUMNS = [
    "Meter ID",
    "Requests Received",
    "Reads Shared",
    "Difference",
  ];

  const rowsContent = reportData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "30px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.meterId}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.requestsReceived}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.readsShared}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.difference}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      {/* <Card className="meters_title_card">
        <Typography variant="h5">Billing Report</Typography>
      </Card> */}

      <Card sx={{ mt: "20px", maxHeight: "400px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Billing Report</Typography>
              <Stack direction={"row"} alignItems={"center"}>
                <Box mr={"20px"}>
                  <CustomSearch
                    className={"meter-command_report_top_card_search"}
                    placeholder={"Enter Meter Id"}
                    isIcon={false}
                  />
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "0.32px",
                    padding: "1px 8px",
                    color: "#ffffff",
                    width: "fit-content",
                  }}
                >
                  <Stack
                    direction={"row"}
                    justifyContent={"center"}
                    gap={"5px"}
                    alignItems={"center"}
                    px={"5px"}
                  >
                    <Typography>
                      {formatDate(startDate, "D MMMM YYYY")} -{" "}
                    </Typography>
                    <Typography>
                      {formatDate(endDate, "D MMMM YYYY")}
                    </Typography>
                  </Stack>
                </Box>
                <IconButton
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                  <Calendar2 />
                </IconButton>
                <Box
                  sx={{
                    width: "1px",
                    height: "31px",
                    background: "#CACDD7",
                    margin: "0px 20px",
                  }}
                ></Box>
                {/* <DatePickerInput
                  placeholder={"dd/mm/yyyy"}
                  width={"fit-content"}
                />
                <DatePickerInput
                  placeholder={"dd/mm/yyyy"}
                  width={"fit-content"}
                /> */}
                <ContainedButton
                  label={"Apply Filters"}
                  sx={{
                    width: "fit-content",
                    color: "#ffffff",
                    boxShadow: "0",
                  }}
                />
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <BillingReportBarChart reportData={reportData} />
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Report Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  color: "#ffffff",
                  boxShadow: "0",
                }}
                onClick={downloadCSV}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent
          className="no-scrollbar"
          sx={{ overflow: "auto", maxHeight: "600px", padding: "0" }}
        >
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={reportData?.length}
          />
        </CardContent>
      </Card>

      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        startDate={startDate}
        endDate={endDate}
        handleDateChange={handleDateChange}
        timeRange={timeRange}
        setTimeRange={(value) => {
          if (Array.isArray(value)) {
            setTimeRange([value[0], value[1]]);
          } else {
            setTimeRange([value, value]);
          }
        }}
        handleDateSubmit={handleDateSubmit}
      />
    </Layout>
  );
};

export default BillingReport;
